/**
 * PayrollReport — Raporti i Pagave për një listë mujore.
 *
 * Dy defekte e mbanin këtë faqe bosh nga kuptimi:
 *
 *   1. Emrat. Rreshtat merreshin nga `GET /api/salaries?listId=…`, por ajo rrugë
 *      është CRUD-i gjenerik: `SELECT * FROM salaries` — pa bashkim me punëtorët
 *      dhe pa e parë fare `listId`. Pra faqja shkarkonte ÇDO rresht page të ÇDO
 *      liste dhe i shfaqte të gjithë me emrin «—». Tani lexon
 *      `getSalariesByListId`, e njëjta pyetje që përdor fletëpagesa: emri,
 *      pozita, numri personal dhe llogaria vijnë bashkë me shumat.
 *   2. Shumat. Tregoheshin gjashtë kolona nga njëzet. Mungonin paga bazë, orët
 *      shtesë, sigurimi shëndetësor dhe ndalesat — pra pikërisht ato që e
 *      shpjegojnë pse bruto nuk bëhet neto. Neto lexohej si `NetSalary` i
 *      papërpunuar, ndaj çdo rresht ku ajo s'ishte ruajtur dilte 0 €; tani
 *      llogaritet me `payslipNet`, të njëjtin rregull si fletëpagesa.
 *
 * Eksporti është dokument, jo dump: Excel dhe PDF me kokë kompanie, kolona të
 * grupuara, total dhe vend nënshkrimi (`utils/payrollReportExport`).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Avatar, Box, Typography, TextField, MenuItem, Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import apiService   from '../../services/apiServices';
import PageHeader   from '../common/PageHeader';
import { payslipNet } from '../../utils/payrollRegisterKS';
import { money } from '../../utils/reportSheetExport';
import { payrollLayout, exportPayrollExcel, exportPayrollPdf, printPayroll } from '../../utils/payrollReportExport';
import {
  ReportPage, ReportFilters, StatTiles, ReportCard, ReportTotals,
  ExportMenu, LoadingRow, EmptyRow,
} from './ReportKit';

const fmt = (v) => `${money(v)} €`;
const num = (v) => Number(v) || 0;
const initials = (name) => (name || '').split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2);

export default function PayrollReport() {
  const { t } = useTranslation();
  const [loading,  setLoading]  = useState(false);
  const [lists,    setLists]    = useState([]);
  const [selList,  setSelList]  = useState('');
  const [salaries, setSalaries] = useState([]);
  const [fDept,    setFDept]    = useState('');
  const [departments, setDepartments] = useState([]);
  const [workers,  setWorkers]  = useState([]);
  const [company,  setCompany]  = useState({});

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, dRes, wRes, sRes] = await Promise.all([
        apiService().get('salariesmonthlylists'),
        apiService().get('departments'),
        apiService().get('workers'),
        apiService().get('settings'),
      ]);
      const ls = (lRes.success ? lRes.data : []) || [];
      ls.sort((a, b) => (b.year - a.year) || (b.month - a.month));
      setLists(ls);
      setDepartments((dRes.success ? dRes.data : []) || []);
      setWorkers((wRes.success ? wRes.data : []) || []);
      if (sRes?.success && sRes.data?.[0]) setCompany(sRes.data[0]);
      if (ls.length > 0) setSelList((cur) => cur || String(ls[0].id));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  /**
   * Rreshtat e listës së zgjedhur.
   * `getSalariesByListId` — jo `get('salaries?listId=…')`: e dyta e shpërfill
   * filtrin dhe s'e bashkon punëtorin, ndaj kthente çdo pagë pa asnjë emër.
   */
  const loadSalaries = useCallback(async (listId) => {
    if (!listId) { setSalaries([]); return; }
    setLoading(true);
    try {
      const res = await apiService().query('getSalariesByListId', `id=${listId}`);
      setSalaries((res?.success ? res.data : []) || []);
    } catch (e) { console.error(e); setSalaries([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSalaries(selList); }, [selList, loadSalaries]);

  const workerById = useCallback((id) => workers.find((w) => String(w.Id || w.id) === String(id)), [workers]);
  const wDeptId    = useCallback((id) => { const w = workerById(id); return w ? String(w.Department ?? w.department_id ?? '') : ''; }, [workerById]);
  const wDeptName  = useCallback((id) => { const w = workerById(id); return w ? (w.DepartmentName || '') : ''; }, [workerById]);
  const wField     = useCallback((id, k) => { const w = workerById(id); return w ? (w[k] || '') : ''; }, [workerById]);

  const rows = useMemo(() => {
    let s = salaries;
    if (fDept) s = s.filter((r) => wDeptId(r.WorkerId || r.workerId) === String(fDept));
    return s.map((r) => {
      const id       = r.WorkerId || r.workerId;
      const base     = num(r.Salary ?? r.GrossSalary);
      const overtime = num(r.HourSalary);
      const bonus    = num(r.Bonus);
      return {
        id,
        // Emri vjen nga pyetja; punëtori mbetet rezervë për rreshtat e vjetër
        // ku bashkimi s'jep asgjë — më mirë emër nga lista se një vizë.
        name:     r.FullName || `${r.FirstName || ''} ${r.LastName || ''}`.trim()
                  || `${wField(id, 'FirstName')} ${wField(id, 'LastName')}`.trim() || '—',
        nid:      r.NID || r.PersonalNumber || wField(id, 'PersonalNumber') || '',
        position: r.Position || wField(id, 'Position') || '',
        dept:     wDeptName(id),
        base,
        overtime,
        bonus,
        // Bruto si te regjistri zyrtar: bazë + orë shtesë + bonus.
        gross:    base + overtime + bonus,
        pensEmp:  num(r.PensionEmployee ?? r.employeeTaxFee),
        pensEr:   num(r.PensionEmployer ?? r.employerTaxFee),
        tax:      num(r.Tax ?? r.taxes),
        health:   num(r.HealthInsEmployee),
        advance:  num(r.Advances),
        penalty:  num(r.Penalties),
        net:      payslipNet(r),
      };
    }).sort((a, b) => a.name.localeCompare(b.name)).map((r, i) => ({ ...r, no: i + 1 }));
  }, [salaries, fDept, wDeptId, wDeptName, wField]);

  const totals = useMemo(() => {
    const s = (k) => rows.reduce((a, r) => a + r[k], 0);
    const gross = s('gross');
    const pensEr = s('pensEr');
    return {
      gross, pensEr,
      base: s('base'), overtime: s('overtime'), bonus: s('bonus'),
      pensEmp: s('pensEmp'), tax: s('tax'), health: s('health'),
      advance: s('advance'), penalty: s('penalty'), net: s('net'),
      deductions: s('pensEmp') + s('tax') + s('health') + s('advance') + s('penalty'),
      employerCost: gross + pensEr,
    };
  }, [rows]);

  const list       = lists.find((l) => String(l.id) === String(selList));
  const listLabel  = (l) => `${t(`report.month_${l.month || l.Month || 1}`)} ${l.year || l.Year}`;
  const period     = list ? listLabel(list) : '—';
  const listStatus = (list?.status || '').toUpperCase();

  const layout = () => payrollLayout(rows, {
    company,
    period,
    status: listStatus,
    t,
    fileBase: `raporti-i-pagave-${list?.year || ''}-${String(list?.month || '').padStart(2, '0')}`,
  });

  // Kufijtë e blloqeve — të njëjtat grupe si te dokumenti: të ardhurat,
  // kontributet, ndalesat, neto. Një vijë e hollë mjafton; kokë e dyfishtë do
  // të kërkonte dy rreshta ngjitës mbi një tabelë që tashmë rrëshqet anash.
  const sep = { borderLeft: '1px solid', borderLeftColor: 'divider' };

  const COLS = [
    { label: t('report.worker'),          key: 'name' },
    { label: t('report.position'),        key: 'position' },
    { label: t('report.department'),      key: 'dept' },
    { label: t('report.baseSalary'),      key: 'base',     money: true, group: true },
    { label: t('report.overtime'),        key: 'overtime', money: true },
    { label: t('report.bonus'),           key: 'bonus',    money: true },
    { label: t('report.gross'),           key: 'gross',    money: true, bold: true },
    { label: t('report.pensionWorker'),   key: 'pensEmp',  money: true, group: true },
    { label: t('report.pensionEmployer'), key: 'pensEr',   money: true },
    { label: t('report.tax'),             key: 'tax',      money: true, group: true },
    { label: t('report.healthIns'),       key: 'health',   money: true },
    { label: t('report.advancements'),    key: 'advance',  money: true },
    { label: t('report.penalties'),       key: 'penalty',  money: true },
    { label: t('report.net'),             key: 'net',      money: true, bold: true, group: true },
  ];

  return (
    <ReportPage>
      <PageHeader title={t('reports.payroll')} subtitle={t('reports.payrollDesc')} />

      <ReportFilters onRefresh={loadLists} onReset={() => setFDept('')} dirty={Boolean(fDept)}>
        <TextField select size="small" label={t('report.selectPayrollList')} value={selList} onChange={(e) => setSelList(e.target.value)}>
          <MenuItem value=""><em>{t('report.selectMonth')}</em></MenuItem>
          {lists.map((l) => <MenuItem key={l.id} value={String(l.id)}>{listLabel(l)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t('report.department')} value={fDept} onChange={(e) => setFDept(e.target.value)}>
          <MenuItem value=""><em>{t('report.allDepartments')}</em></MenuItem>
          {departments.map((d) => <MenuItem key={d.id || d.Id} value={String(d.id || d.Id)}>{d.name || d.Name}</MenuItem>)}
        </TextField>
      </ReportFilters>

      <StatTiles
        columns={4}
        items={[
          { label: t('report.workers'),          value: rows.length },
          { label: t('report.gross'),            value: fmt(totals.gross) },
          { label: t('report.deductions'),       value: fmt(totals.deductions) },
          { label: t('report.net'),              value: fmt(totals.net), accent: true },
          { label: t('report.tax'),              value: fmt(totals.tax) },
          { label: t('report.pensionWorker'),    value: fmt(totals.pensEmp) },
          { label: t('report.pensionEmployer'),  value: fmt(totals.pensEr) },
          { label: t('report.employerCost'),     value: fmt(totals.employerCost) },
        ]}
      />

      <ReportCard
        title={period}
        count={rows.length}
        countLabel={t('report.workers')}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {listStatus && (
              <Chip size="small" variant="outlined"
                color={listStatus === 'LOCKED' ? 'success' : 'default'}
                label={listStatus} sx={{ height: 20, fontSize: '0.6875rem' }} />
            )}
            <ExportMenu
              onExcel={() => exportPayrollExcel(layout())}
              onPdf={() => exportPayrollPdf(layout())}
              onPrint={() => printPayroll(layout())}
              disabled={!rows.length}
            />
          </Stack>
        }
        footer={rows.length > 0 && (
          <ReportTotals
            items={[
              { label: t('report.totalBruto'),   value: fmt(totals.gross) },
              { label: `${t('report.deductions')}:`, value: fmt(totals.deductions) },
              { label: t('report.totalNeto'),    value: fmt(totals.net) },
              { label: t('report.totalPension'), value: fmt(totals.pensEmp + totals.pensEr) },
              { label: `${t('report.employerCost')}:`, value: fmt(totals.employerCost) },
            ]}
          />
        )}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLS.map((c) => (
                <TableCell key={c.key} className={c.money ? 'num' : undefined} sx={c.group ? sep : undefined}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingRow colSpan={COLS.length} />
            ) : rows.length === 0 ? (
              <EmptyRow colSpan={COLS.length} message={selList ? t('report.noPayrollForList') : t('report.selectPayrollList')} />
            ) : rows.map((r, i) => (
              <TableRow key={`${r.id ?? 'w'}-${i}`} hover>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main' }}>{initials(r.name)}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{r.name}</Typography>
                      {r.nid && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{r.nid}</Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.position || '—'}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.dept || '—'}</TableCell>
                <TableCell className="num" sx={sep}>{fmt(r.base)}</TableCell>
                <TableCell className="num" sx={{ color: r.overtime > 0 ? 'text.primary' : 'text.disabled' }}>{r.overtime > 0 ? fmt(r.overtime) : '—'}</TableCell>
                <TableCell className="num" sx={{ color: r.bonus > 0 ? 'success.main' : 'text.disabled' }}>{r.bonus > 0 ? fmt(r.bonus) : '—'}</TableCell>
                <TableCell className="num" sx={{ fontWeight: 700 }}>{fmt(r.gross)}</TableCell>
                <TableCell className="num" sx={sep}>{fmt(r.pensEmp)}</TableCell>
                <TableCell className="num" sx={{ color: 'text.secondary' }}>{fmt(r.pensEr)}</TableCell>
                <TableCell className="num" sx={sep}>{fmt(r.tax)}</TableCell>
                <TableCell className="num" sx={{ color: r.health > 0 ? 'text.primary' : 'text.disabled' }}>{r.health > 0 ? fmt(r.health) : '—'}</TableCell>
                <TableCell className="num" sx={{ color: r.advance > 0 ? 'warning.main' : 'text.disabled' }}>{r.advance > 0 ? fmt(r.advance) : '—'}</TableCell>
                <TableCell className="num" sx={{ color: r.penalty > 0 ? 'error.main' : 'text.disabled' }}>{r.penalty > 0 ? fmt(r.penalty) : '—'}</TableCell>
                <TableCell className="num" sx={{ ...sep, fontWeight: 700, color: 'success.main' }}>{fmt(r.net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportCard>
    </ReportPage>
  );
}
